import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { ArrowLeft } from 'lucide-react'
import SuccessModal from '@/shared/components/modals/SuccessModal'
import ShareLinkModal from '@/shared/components/modals/ShareLinkModal'
import { idSaiParaAlta, validarIdSai } from '@/features/proveedores/lib/idSai'
import { createSupplierInvitation, type CreateSupplierInvitationResponse } from '@/features/proveedores/api/supplierInvitationService'
import { SERVICE_TYPES } from '@/constants/supplierConstants'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface CreateSupplierInvitationProps {
  onBack: () => void
  onSuccess: () => void
}

interface FormData {
  legalName: string
  commercialName: string
  rfc: string
  serviceTypeId: string
  contactPhone: string
  contactEmail: string
  saiSupplierId: string
}

const CreateSupplierInvitation = ({ onBack, onSuccess }: CreateSupplierInvitationProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [invitationData, setInvitationData] = useState<CreateSupplierInvitationResponse | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)

  const { 
    register, 
    control,
    handleSubmit: handleFormSubmit, 
    watch, 
    formState: { errors }
  } = useForm<FormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      legalName: '',
      commercialName: '',
      rfc: '',
      serviceTypeId: '',
      contactPhone: '',
      contactEmail: '',
      saiSupplierId: ''
    }
  })

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)
    setApiError(null)

    try {
      const payload = {
        legalName: data.legalName,
        tradeName: data.commercialName,
        rfc: data.rfc,
        serviceTypeId: data.serviceTypeId,
        contactPhone: data.contactPhone,
        email: data.contactEmail,
        saiSupplierId: idSaiParaAlta(data.saiSupplierId)
      }

      const response = await createSupplierInvitation(payload)
      setInvitationData(response)
      setShowSuccessModal(true)
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al crear invitación:', error)
      setApiError(
        error.message || 'Error al crear la invitación. Por favor intenta nuevamente.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false)
    setShowLinkModal(true)
  }

  const handleCloseLinkModal = () => {
    setShowLinkModal(false)
    onSuccess()
  }

  const generateInvitationLink = (): string => {
    if (!invitationData?.token) return ''
    // El admin vive en otro dominio que el portal del proveedor, asi que
    // window.location.origin generaba un enlace al dominio interno y el
    // proveedor caia en el login del personal. En local, donde los dos corren
    // en el mismo puerto, el origen sigue sirviendo.
    const portal = import.meta.env.VITE_SUPPLIER_PORTAL_URL || window.location.origin
    return `${portal.replace(/\/$/, '')}/proveedores/registro-invitacion?token=${invitationData.token}`
  }

  return (
    <>
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseSuccessModal}
        title="El formulario ha sido enviado correctamente"
        message="El formulario ha sido enviado correctamente a pre-valoración. En breve recibirá los resultados."
      />

      <ShareLinkModal
        isOpen={showLinkModal}
        onClose={handleCloseLinkModal}
        invitationLink={generateInvitationLink()}
        supplierName={invitationData?.legalName}
      />

      <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={onBack}
            className={estiloDeBoton('secundario', 'mb-4')}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Crear invitación</h1>
          <p className="text-gray-600 mt-1">
            Llena los datos del siguiente formulario para crear una invitación de proveedor.
          </p>
        </div>

        {/* API Error Message */}
        {apiError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-2">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm text-red-700">{apiError}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleFormSubmit(onSubmit)}>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Section Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Datos generales</h2>
            </div>

            {/* Form Fields */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Razón social */}
                <div>
                  <label htmlFor="legalName" className="block text-sm font-medium text-gray-700 mb-2">
                    Razón social <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      {...register('legalName', {
                        required: 'La razón social es requerida',
                        minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                      })}
                      className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        errors.legalName
                          ? 'border-red-500 ring-2 ring-red-500 bg-red-50'
                          : watch('legalName') && !errors.legalName
                            ? 'border-green-500 ring-2 ring-green-500 bg-green-50'
                            : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="Ingresa la razón social"
                    />
                    {watch('legalName') && !errors.legalName && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xl">✓</span>
                    )}
                    {errors.legalName && watch('legalName') && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-xl">✗</span>
                    )}
                  </div>
                  <p className={`mt-1 text-xs font-medium ${
                    errors.legalName 
                      ? 'text-red-600' 
                      : watch('legalName') && !errors.legalName
                        ? 'text-green-600'
                        : 'text-gray-500'
                  }`}>
                    {errors.legalName?.message || 
                     (watch('legalName') && !errors.legalName 
                       ? '✓ Razón social válida' 
                       : 'Mínimo 3 caracteres'
                     )}
                  </p>
                </div>

                {/* Nombre comercial */}
                <div>
                  <label htmlFor="commercialName" className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre comercial <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      {...register('commercialName', {
                        required: 'El nombre comercial es requerido',
                        minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                      })}
                      className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        errors.commercialName
                          ? 'border-red-500 ring-2 ring-red-500 bg-red-50'
                          : watch('commercialName') && !errors.commercialName
                            ? 'border-green-500 ring-2 ring-green-500 bg-green-50'
                            : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="Ingresa el nombre comercial"
                    />
                    {watch('commercialName') && !errors.commercialName && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xl">✓</span>
                    )}
                    {errors.commercialName && watch('commercialName') && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-xl">✗</span>
                    )}
                  </div>
                  <p className={`mt-1 text-xs font-medium ${
                    errors.commercialName 
                      ? 'text-red-600' 
                      : watch('commercialName') && !errors.commercialName
                        ? 'text-green-600'
                        : 'text-gray-500'
                  }`}>
                    {errors.commercialName?.message || 
                     (watch('commercialName') && !errors.commercialName 
                       ? '✓ Nombre comercial válido' 
                       : 'Mínimo 3 caracteres'
                     )}
                  </p>
                </div>

                {/* RFC */}
                <div>
                  <label htmlFor="rfc" className="block text-sm font-medium text-gray-700 mb-2">
                    RFC <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      {...register('rfc', {
                        required: 'El RFC es requerido',
                        pattern: {
                          value: /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/,
                          message: 'RFC inválido. Formato: AAA010101AAA'
                        },
                        onChange: (e) => {
                          e.target.value = e.target.value.toUpperCase()
                        }
                      })}
                      maxLength={13}
                      className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        errors.rfc
                          ? 'border-red-500 ring-2 ring-red-500 bg-red-50'
                          : watch('rfc') && !errors.rfc && watch('rfc').length >= 12
                            ? 'border-green-500 ring-2 ring-green-500 bg-green-50'
                            : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="AAA010101AAA"
                    />
                    {watch('rfc') && !errors.rfc && watch('rfc').length >= 12 && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xl">✓</span>
                    )}
                    {errors.rfc && watch('rfc') && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-xl">✗</span>
                    )}
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className={`font-medium ${
                      errors.rfc 
                        ? 'text-red-600' 
                        : watch('rfc') && !errors.rfc && watch('rfc').length >= 12
                          ? 'text-green-600' 
                          : 'text-gray-500'
                    }`}>
                      {errors.rfc?.message || 
                       (watch('rfc') && !errors.rfc && watch('rfc').length >= 12
                         ? '✓ RFC válido' 
                         : '12-13 caracteres alfanuméricos'
                       )}
                    </span>
                    <span className={`font-medium ${
                      watch('rfc')?.length >= 12 ? 'text-green-600' : 
                      watch('rfc')?.length > 0 ? 'text-blue-600' : 'text-gray-400'
                    }`}>
                      {watch('rfc')?.length || 0}/13
                    </span>
                  </div>
                </div>

                {/* Tipo de servicio */}
                <div>
                  <label htmlFor="serviceTypeId" className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de servicio <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="serviceTypeId"
                    control={control}
                    rules={{ required: 'El tipo de servicio es requerido' }}
                    render={({ field }) => (
                      <SelectBuscable
                        variante="formulario"
                        id="serviceTypeId"
                        name={field.name}
                        value={String(field.value ?? '')}
                        onChange={field.onChange}
                        opciones={SERVICE_TYPES.map((type) => ({ value: String(type.id), label: type.name }))}
                        placeholder="Selecciona un tipo de servicio"
                        placeholderDeBusqueda="Buscar tipo de servicio..."
                        error={Boolean(errors.serviceTypeId)}
                      />
                    )}
                  />
                  <p className={`mt-1 text-xs font-medium ${
                    errors.serviceTypeId 
                      ? 'text-red-600' 
                      : watch('serviceTypeId') && !errors.serviceTypeId
                        ? 'text-green-600'
                        : 'text-gray-500'
                  }`}>
                    {errors.serviceTypeId?.message || 
                     (watch('serviceTypeId') && !errors.serviceTypeId 
                       ? '✓ Tipo de servicio seleccionado' 
                       : 'Selecciona una opción'
                     )}
                  </p>
                </div>

                {/* Contacto principal */}
                <div>
                  <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-2">
                    Contacto principal <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      {...register('contactPhone', {
                        required: 'El teléfono es requerido',
                        pattern: {
                          value: /^\d{10}$/,
                          message: 'Debe contener exactamente 10 dígitos'
                        }
                      })}
                      maxLength={10}
                      className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        errors.contactPhone
                          ? 'border-red-500 ring-2 ring-red-500 bg-red-50'
                          : watch('contactPhone')?.length === 10 && !errors.contactPhone
                            ? 'border-green-500 ring-2 ring-green-500 bg-green-50'
                            : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="5551234567"
                    />
                    {watch('contactPhone')?.length === 10 && !errors.contactPhone && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xl">✓</span>
                    )}
                    {errors.contactPhone && watch('contactPhone') && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-xl">✗</span>
                    )}
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className={`font-medium ${
                      errors.contactPhone 
                        ? 'text-red-600' 
                        : watch('contactPhone')?.length === 10 
                          ? 'text-green-600' 
                          : 'text-gray-500'
                    }`}>
                      {errors.contactPhone?.message || 
                       (watch('contactPhone')?.length === 10 ? '✓ Teléfono válido' : '10 dígitos numéricos')}
                    </span>
                    <span className={`font-medium ${
                      watch('contactPhone')?.length === 10 ? 'text-green-600' : 
                      watch('contactPhone')?.length > 0 ? 'text-blue-600' : 'text-gray-400'
                    }`}>
                      {watch('contactPhone')?.length || 0}/10
                    </span>
                  </div>
                </div>

                {/* Correo de contacto */}
                <div>
                  <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Correo de contacto <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      {...register('contactEmail', {
                        required: 'El correo es requerido',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Correo electrónico inválido'
                        }
                      })}
                      className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        errors.contactEmail
                          ? 'border-red-500 ring-2 ring-red-500 bg-red-50'
                          : watch('contactEmail') && !errors.contactEmail
                            ? 'border-green-500 ring-2 ring-green-500 bg-green-50'
                            : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="jose@example.com"
                    />
                    {watch('contactEmail') && !errors.contactEmail && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xl">✓</span>
                    )}
                    {errors.contactEmail && watch('contactEmail') && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-xl">✗</span>
                    )}
                  </div>
                  <p className={`mt-1 text-xs font-medium ${
                    errors.contactEmail 
                      ? 'text-red-600' 
                      : watch('contactEmail') && !errors.contactEmail
                        ? 'text-green-600'
                        : 'text-gray-500'
                  }`}>
                    {errors.contactEmail?.message || 
                     (watch('contactEmail') && !errors.contactEmail 
                       ? '✓ Email válido' 
                       : 'Formato: ejemplo@correo.com'
                     )}
                  </p>
                </div>

                {/* ID interno (SAI) */}
                <div>
                  <label htmlFor="saiSupplierId" className="block text-sm font-medium text-gray-700 mb-2">
                    ID interno (SAI)
                  </label>
                  <input
                    id="saiSupplierId"
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    {...register('saiSupplierId', { validate: validarIdSai })}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                      errors.saiSupplierId
                        ? 'border-red-500 ring-2 ring-red-500 bg-red-50'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    placeholder="42"
                  />
                  <p className={`mt-1 text-xs font-medium ${errors.saiSupplierId ? 'text-red-600' : 'text-gray-500'}`}>
                    {errors.saiSupplierId?.message || 'Opcional. Número del proveedor en SAI, de 1 a 99999.'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onBack}
              className={estiloDeBoton('secundario')}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={estiloDeBoton('primario')}
            >
              {isSubmitting ? 'Enviando...' : 'Enviar a pre-valoración'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  )
}

export default CreateSupplierInvitation
